import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { API_BASE_URL } from '../config';
import './MomentumRadar.css';
import RefreshButton from './RefreshButton';
import WatchlistStar from './WatchlistStar';

const MomentumRadar = ({ onBack }) => {
  // Single active feature state
  const [activeFeature, setActiveFeature] = useState('lom'); // 'lom' | 'highlow' | 'spike' | '3percent'

  // Data states for each feature
  const [lomStocks, setLomStocks] = useState([]);
  const [highLowStocks, setHighLowStocks] = useState([]);
  const [spikeStocks, setSpikeStocks] = useState([]);
  const [threePercentData, setThreePercentData] = useState({ topPerformers: [], lowPerformers: [] });

  // Loading and error states
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Filter states
  const [trend, setTrend] = useState(''); // '' | 'high' | 'low'

  // Sub-filter states for each feature
  const [lomTimeframe, setLomTimeframe] = useState('5min');
  const [lomTrend, setLomTrend] = useState('high');
  const [spikeTimeframe, setSpikeTimeframe] = useState('5min');
  const [spikeTrend, setSpikeTrend] = useState('high');
  const [highLowDirection, setHighLowDirection] = useState('ALL');
  const [threePercentDirection, setThreePercentDirection] = useState('ALL');

  // Theme state
  const [isDarkMode, setIsDarkMode] = useState(false);

  useEffect(() => {
    const checkDarkMode = () => {
      setIsDarkMode(document.documentElement.classList.contains('darkmode'));
    };
    checkDarkMode();
    const observer = new MutationObserver(checkDarkMode);
    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ['class']
    });
    return () => observer.disconnect();
  }, []);

  const formatTime = (datetime) => {
    if (!datetime) return "";
    const d = new Date(datetime);
    return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
  };

  const formatNumber = useCallback((num) => {
    if (num === null || num === undefined) return 'N/A';
    const number = Number(num);
    return isNaN(number) ? 'N/A' : number.toFixed(2);
  }, []);

  // Fetch Short Term LOM Data
  const fetchLomData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/short-term-lom`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          timeframe: lomTimeframe === '5min' ? '5min' : '15min',
          trend: lomTrend,
          category: 'fo'
        })
      });

      if (!response.ok) {
        throw new Error('Failed to fetch short term LOM data');
      }

      const result = await response.json();
      if (result.status === 'success' && Array.isArray(result.data)) {
        setLomStocks(result.data);
      } else {
        setLomStocks([]);
      }
    } catch (err) {
      console.error('Error fetching short term LOM data:', err);
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [lomTimeframe, lomTrend]);

  // Fetch High/Low Data
  const fetchHighLowData = useCallback(async (retryCount = 0) => {
    const MAX_RETRIES = 2;
    const RETRY_DELAY = 2000;
    
    setLoading(true);
    setError("");
    
    try {
      const response = await fetch(`${API_BASE_URL}/breakouts`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ stock_list: [] })
      });
      
      const responseData = await response.json();
      
      if (!response.ok) {
        throw new Error(responseData.message || `Server error: ${response.status}`);
      }
      
      if (responseData && Array.isArray(responseData.data)) {
        setHighLowStocks(responseData.data);
      } else {
        throw new Error('Invalid data format received from server');
      }
    } catch (err) {
      if (retryCount < MAX_RETRIES && 
          (err.message.includes('Network') || err.message.includes('Failed to fetch'))) {
        await new Promise(resolve => setTimeout(resolve, RETRY_DELAY));
        return fetchHighLowData(retryCount + 1);
      }
      setError(`Failed to load data: ${err.message}`);
      setHighLowStocks([]);
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch Momentum Spike Data
  const fetchSpikeData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await fetch(`${API_BASE_URL}/stockanalyze`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Accept': 'application/json'
        },
        body: JSON.stringify({ 
          timeframe: spikeTimeframe,
          trend: spikeTrend,
          count: 50
        })
      });

      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`HTTP error! status: ${response.status}, ${errorText}`);
      }

      const data = await response.json();
      if (data && Array.isArray(data.data) && data.data.length > 0) {
        setSpikeStocks(data.data);
      } else {
        setSpikeStocks([]);
      }
    } catch (err) {
      console.error('Error in fetchData:', err);
      setError(err.message || 'Failed to fetch data');
    } finally {
      setLoading(false);
    }
  }, [spikeTimeframe, spikeTrend]);

  // Fetch 3% Rule Data
  const fetchThreePercentData = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const response = await fetch(`${API_BASE_URL}/breakout-three-percent`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ category: 'fo' })
      });
      
      const data = await response.json();
      setThreePercentData({
        topPerformers: data.data?.top_performers || [],
        lowPerformers: data.data?.low_performers || []
      });
    } catch (err) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  }, []);

  // Fetch data when active feature changes
  useEffect(() => {
    switch (activeFeature) {
      case 'lom':
        fetchLomData();
        break;
      case 'highlow':
        fetchHighLowData();
        break;
      case 'spike':
        fetchSpikeData();
        break;
      case '3percent':
        fetchThreePercentData();
        break;
      default:
        break;
    }
  }, [activeFeature, fetchLomData, fetchHighLowData, fetchSpikeData, fetchThreePercentData]);

  // Refresh handler
  const handleRefresh = () => {
    switch (activeFeature) {
      case 'lom':
        fetchLomData();
        break;
      case 'highlow':
        fetchHighLowData();
        break;
      case 'spike':
        fetchSpikeData();
        break;
      case '3percent':
        fetchThreePercentData();
        break;
      default:
        break;
    }
  };

  // Filtered data for High/Low
  const filteredHighLowStocks = useMemo(() => {
    let list = highLowStocks;
    if (highLowDirection === "UP") list = list.filter((s) => s.chart === "up");
    else if (highLowDirection === "DOWN") list = list.filter((s) => s.chart === "down");
    return list;
  }, [highLowStocks, highLowDirection]);

  // Filtered data for 3% Rule
  const filteredThreePercent = useMemo(() => {
    const top = threePercentData.topPerformers.filter(stock => stock.percentage_change >= 0);
    const low = threePercentData.lowPerformers.filter(stock => stock.percentage_change < 0);
    return {
      showTop: threePercentDirection === 'ALL' || threePercentDirection === 'UP',
      showLow: threePercentDirection === 'ALL' || threePercentDirection === 'DOWN',
      top: top,
      low: low
    };
  }, [threePercentData, threePercentDirection]);

  // Render sub-filters based on active feature
  const renderSubFilters = () => {
    switch (activeFeature) {
      case 'lom':
        return (
          <div className="sub-filters">
            <div className="filter-group">
              <span>Timeframe:</span>
              <button 
                className={`sub-filter-btn ${lomTimeframe === '5min' ? 'active' : ''}`}
                onClick={() => setLomTimeframe('5min')}
              >
                5 Min
              </button>
              <button 
                className={`sub-filter-btn ${lomTimeframe === '15min' ? 'active' : ''}`}
                onClick={() => setLomTimeframe('15min')}
              >
                15 Min
              </button>
            </div>
            <div className="filter-group">
              <span>Trend:</span>
              <button 
                className={`sub-filter-btn ${lomTrend === 'high' ? 'active' : ''}`}
                onClick={() => setLomTrend('high')}
              >
                UP
              </button>
              <button 
                className={`sub-filter-btn ${lomTrend === 'low' ? 'active' : ''}`}
                onClick={() => setLomTrend('low')}
              >
                DOWN
              </button>
            </div>
          </div>
        );
      case 'highlow':
        return (
          <div className="sub-filters">
            <span>Direction:</span>
            <button 
              className={`sub-filter-btn ${highLowDirection === 'UP' ? 'active' : ''}`}
              onClick={() => setHighLowDirection('UP')}
            >
              UP
            </button>
            <button 
              className={`sub-filter-btn ${highLowDirection === 'DOWN' ? 'active' : ''}`}
              onClick={() => setHighLowDirection('DOWN')}
            >
              DOWN
            </button>
            <button 
              className={`sub-filter-btn ${highLowDirection === 'ALL' ? 'active' : ''}`}
              onClick={() => setHighLowDirection('ALL')}
            >
              ALL
            </button>
          </div>
        );
      case 'spike':
        return (
          <div className="sub-filters">
            <div className="filter-group">
              <span>Timeframe:</span>
              <button 
                className={`sub-filter-btn ${spikeTimeframe === '5min' ? 'active' : ''}`}
                onClick={() => setSpikeTimeframe('5min')}
              >
                5 Min
              </button>
              <button 
                className={`sub-filter-btn ${spikeTimeframe === '15min' ? 'active' : ''}`}
                onClick={() => setSpikeTimeframe('15min')}
              >
                15 Min
              </button>
            </div>
            <div className="filter-group">
              <span>Trend:</span>
              <button 
                className={`sub-filter-btn ${spikeTrend === 'high' ? 'active' : ''}`}
                onClick={() => setSpikeTrend('high')}
              >
                UP
              </button>
              <button 
                className={`sub-filter-btn ${spikeTrend === 'low' ? 'active' : ''}`}
                onClick={() => setSpikeTrend('low')}
              >
                DOWN
              </button>
            </div>
          </div>
        );
      case '3percent':
        return (
          <div className="sub-filters">
            <span>Filter:</span>
            <button 
              className={`sub-filter-btn ${threePercentDirection === 'UP' ? 'active' : ''}`}
              onClick={() => setThreePercentDirection('UP')}
            >
              UP
            </button>
            <button 
              className={`sub-filter-btn ${threePercentDirection === 'DOWN' ? 'active' : ''}`}
              onClick={() => setThreePercentDirection('DOWN')}
            >
              DOWN
            </button>
            <button 
              className={`sub-filter-btn ${threePercentDirection === 'ALL' ? 'active' : ''}`}
              onClick={() => setThreePercentDirection('ALL')}
            >
              ALL
            </button>
          </div>
        );
      default:
        return null;
    }
  };

  // Render table based on active feature
  const renderTable = () => {
    if (loading) {
      return <div className="loading">⏳ Loading stock data...</div>;
    }

    if (error) {
      return <div className="error-message">{error}</div>;
    }

    switch (activeFeature) {
      case 'lom':
        return (
          <div className="table-responsive">
            <table className="stocks-table">
              <thead>
                <tr>
                  <th>Symbol</th>
                  <th>LOM Time</th>
                  <th>% Change</th>
                  <th>LTP</th>
                </tr>
              </thead>
              <tbody>
                {lomStocks.length > 0 ? (
                  lomStocks.map((stock, index) => {
                    const lomTime = new Date(stock.end_time);
                    const formattedTime = lomTime.toLocaleTimeString('en-US', { 
                      hour: 'numeric', 
                      minute: '2-digit',
                      hour12: true
                    });
                    const percentChange = stock.percent_change || 0;
                    const isPositive = percentChange >= 0;
                    const formattedPercent = (isPositive ? '+' : '') + percentChange.toFixed(2) + '%';
                    const formattedLTP = typeof stock.ltp === 'number' 
                      ? stock.ltp.toLocaleString('en-IN', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
                      : stock.ltp || 'N/A';
                    
                    return (
                      <tr key={index}>
                        <td>
                          <span className="stock-symbol">
                            <WatchlistStar 
                              stock={{
                                symbol: stock.symbol,
                                name: stock.symbol
                              }} 
                            />
                            {stock.symbol}
                          </span>
                        </td>
                        <td>{formattedTime}</td>
                        <td className={isPositive ? 'positive' : 'negative'}>
                          {formattedPercent}
                        </td>
                        <td>{formattedLTP}</td>
                      </tr>
                    );
                  })
                ) : (
                  <tr>
                    <td colSpan="4" className="no-data">No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case 'highlow':
        return (
          <div className="hl-table-section">
            <div className="hl-table">
              <div className="hl-row hl-header-row">
                <div className="hl-cell">⭐</div>
                <div className="hl-cell">Name</div>
                <div className="hl-cell">Date & Time</div>
                <div className="hl-cell">Percentage</div>
                <div className="hl-cell">Percentage After B.O</div>
                <div className="hl-cell">SIGNALS</div>
              </div>
              {filteredHighLowStocks.length === 0 ? (
                <div className="hl-row">
                  <div className="hl-cell">No results</div>
                </div>
              ) : (
                filteredHighLowStocks.map((stock) => (
                  <div key={stock.id} className="hl-row">
                    <div className="hl-cell">
                      <WatchlistStar 
                        stock={{
                          symbol: stock.symbol,
                          name: stock.symbol
                        }} 
                        size="18px"
                      />
                    </div>
                    <div className="hl-cell">{stock.symbol}</div>
                    <div className="hl-cell">{stock.breakout_time}</div>
                    <div className="hl-cell">
                      <span className={stock.breakout_pct && !String(stock.breakout_pct).includes('-') ? 'positive' : 'negative'}>
                        {stock.breakout_pct !== "" ? `${String(stock.breakout_pct).includes('-') ? '' : '+'}${stock.breakout_pct}%` : "—"}
                      </span>
                    </div>
                    <div className="hl-cell">
                      <span className={stock.after_bo && !String(stock.after_bo).includes('-') ? 'positive' : 'negative'}>
                        {stock.after_bo !== "" ? `${String(stock.after_bo).includes('-') ? '' : '+'}${stock.after_bo}%` : "—"}
                      </span>
                    </div>
                    <div className="hl-cell hl-signal-cell">
                      {stock.chart === "up" ? (
                        <span className="signal-up" style={{ color: '#10b981', fontSize: '1.2rem' }}>↑</span>
                      ) : stock.chart === "down" ? (
                        <span className="signal-down" style={{ color: '#ef4444', fontSize: '1.2rem' }}>↓</span>
                      ) : (
                        "—"
                      )}
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        );

      case 'spike':
        return (
          <div className="table-responsive">
            <table className="stocks-table">
              <thead>
                <tr>
                  <th>SYMBOL</th>
                  <th>LTP</th>
                  <th>CHANGE %</th>
                  <th>LOM %</th>
                  <th>TIME</th>
                  <th>SIGNALS</th>
                </tr>
              </thead>
              <tbody>
                {spikeStocks && spikeStocks.length > 0 ? (
                  [...spikeStocks]
                    .sort((a, b) => (b["Change (%)"] || 0) - (a["Change (%)"] || 0))
                    .map((stock, index) => {
                      const change = stock["Change (%)"] || 0;
                      const lom = stock["LOM (%)"] || 0;
                      const ltp = stock["LTP"] || 0;
                      const stockName = stock["Stock"] || 'N/A';
                      const lomTime = stock["LOM Time"];

                      return (
                        <tr key={`${stockName}-${index}`}>
                          <td>
                            <span className="stock-symbol">
                              <WatchlistStar 
                                stock={{
                                  symbol: stockName,
                                  name: stockName
                                }}
                              />
                              {stockName}
                            </span>
                          </td>
                          <td>{formatNumber(ltp)}</td>
                          <td className={change >= 0 ? 'positive' : 'negative'}>
                            {formatNumber(change)}%
                          </td>
                          <td className={lom >= 0 ? 'positive' : 'negative'}>
                            {formatNumber(lom)}%
                          </td>
                          <td>
                            {lomTime ? new Date(lomTime).toLocaleTimeString() : 'N/A'}
                          </td>
                          <td>
                            <span 
                              style={{
                                fontSize: '20px',
                                fontWeight: 'bold',
                                color: change >= 0 ? '#5bcb50' : '#ff2e2e'
                              }}
                            >
                              {change >= 0 ? '↑' : '↓'}
                            </span>
                          </td>
                        </tr>
                      );
                    })
                ) : (
                  <tr>
                    <td colSpan="6" className="text-center">No data available</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        );

      case '3percent':
        return (
          <div className="rule-strategy-table-container">
            <div className="results-count">
              Showing {filteredThreePercent.top.length + filteredThreePercent.low.length} results ({threePercentDirection.toUpperCase()})
            </div>
            <div className="rule-strategy-table">
              {filteredThreePercent.showTop && filteredThreePercent.top.length > 0 && (
                <>
                  <h3>Top Performers ({filteredThreePercent.top.length})</h3>
                  <div className="table-row table-header-row">
                    <div className="table-cell">Stock</div>
                    <div className="table-cell">LTP</div>
                    <div className="table-cell">% Change</div>
                    <div className="table-cell">Proximity to High</div>
                  </div>
                  {filteredThreePercent.top.map((stock, i) => (
                    <div key={i} className="table-row">
                      <div className="table-cell">
                        <WatchlistStar stock={stock} size="16px" />
                        {stock.stock}
                      </div>
                      <div className="table-cell">{stock.ltp}</div>
                      <div className="table-cell positive">{stock.percentage_change}%</div>
                      <div className="table-cell">{stock.proximity_to_high}</div>
                    </div>
                  ))}
                </>
              )}
              {filteredThreePercent.showLow && filteredThreePercent.low.length > 0 && (
                <>
                  <h3>Low Performers ({filteredThreePercent.low.length})</h3>
                  <div className="table-row table-header-row">
                    <div className="table-cell">Stock</div>
                    <div className="table-cell">LTP</div>
                    <div className="table-cell">% Change</div>
                    <div className="table-cell">Proximity to Low</div>
                  </div>
                  {filteredThreePercent.low.map((stock, i) => (
                    <div key={i} className="table-row">
                      <div className="table-cell">
                        <WatchlistStar stock={stock} size="16px" />
                        {stock.stock}
                      </div>
                      <div className="table-cell">{stock.ltp}</div>
                      <div className="table-cell negative">{stock.percentage_change}%</div>
                      <div className="table-cell">{stock.proximity_to_low}</div>
                    </div>
                  ))}
                </>
              )}
              {(!filteredThreePercent.showTop || filteredThreePercent.top.length === 0) && 
               (!filteredThreePercent.showLow || filteredThreePercent.low.length === 0) && (
                <div className="table-empty-state">
                  No stocks match "{threePercentDirection}" filter
                </div>
              )}
            </div>
          </div>
        );

      default:
        return null;
    }
  };

  // Get feature title
  const getFeatureTitle = () => {
    switch (activeFeature) {
      case 'lom': return `LOSS OF MOMENTUM (${lomTrend.toUpperCase()} - ${lomTimeframe})`;
      case 'highlow': return '2 DAY HIGH & LOW';
      case 'spike': return `Momentum Spike (${spikeTrend.toUpperCase()} - ${spikeTimeframe})`;
      case '3percent': return '3% RULE STRATEGY';
      default: return 'Momentum Radar';
    }
  };

  return (
    <div className={`momentum-radar-container ${isDarkMode ? 'dark' : ''}`}>
      <header className="momentum-header">
        <div className="momentum-header-container">
          <div className="logo">
            <img src="images/Final Font Stocks4U1.png" className="logo-text" alt="Stocks4U Logo" />
          </div>
          <div className="momentum-user">
            <RefreshButton 
              onRefresh={handleRefresh} 
              isLoading={loading} 
              size="small"
              className="mr-2"
            />
          </div>
        </div>
      </header>

      <main className="momentum-main">
        <div className="momentum-header-bar s4u-title-row">
          <div className="s4u-page-heading">
            <button className="momentum-back-btn s4u-back-btn" onClick={onBack}>← Back to Dashboard</button>
            <h1>{getFeatureTitle()}</h1>
          </div>
          {/* Top filter buttons - kept as requested */}
          <div className="filter-buttons">
            <h3>Filter By:</h3>
            <button 
              className={`filter-btn ${trend === 'high' ? 'active' : ''}`} 
              onClick={() => setTrend('high')}
            >UP</button>
            <button 
              className={`filter-btn ${trend === 'low' ? 'active' : ''}`}
              onClick={() => setTrend('low')}
            >DOWN</button>
            {trend && (
              <button 
                className="filter-btn clear"
                onClick={() => setTrend('')}
              >
                Clear
              </button>
            )}
          </div>
        </div>

        {/* Feature Categories - Horizontal scrollable on mobile */}
        <div className="spike-sections-container categories">
          <div className="spike-section">
            <button 
              className={`toggle-design-btn ${activeFeature === 'lom' ? 'active' : ''}`}
              onClick={() => setActiveFeature('lom')}
            >
              LOSS OF MOMENTUM (LOM)
            </button>
          </div>
          
          <div className="spike-section">
            <button 
              className={`toggle-design-btn ${activeFeature === 'highlow' ? 'active' : ''}`}
              onClick={() => setActiveFeature('highlow')}
            >
              Switch 2 DAY HIGH & LOW
            </button>
          </div>
          
          <div className="spike-section">
            <button 
              className={`toggle-design-btn ${activeFeature === 'spike' ? 'active' : ''}`}
              onClick={() => setActiveFeature('spike')}
            >
              5 & 15 min Momentum Spike
            </button>
          </div>
          
          <div className="spike-section">
            <button 
              className={`toggle-design-btn ${activeFeature === '3percent' ? 'active' : ''}`}
              onClick={() => setActiveFeature('3percent')}
            >
              3% Rule
            </button>
          </div>
        </div>

        {/* Sub-filters for each feature */}
        {renderSubFilters()}

        {/* Table Content */}
        {renderTable()}
      </main>
    </div>
  );
};

export default MomentumRadar;